/**
 * A table inside a card (TMMAPP-0088, pattern 4). The children sit in a horizontal scroll region
 * that is focusable and named, so a keyboard user can scroll a wide table.
 */
import type { ReactNode } from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { resolveTokens } from '@/theme/theme';

interface TableCardBase {
  /** `h2` unless the card sits under another section heading. */
  heading?: 'h2' | 'h3';
  subtitle?: ReactNode;
  toolbar?: ReactNode;
  footnote?: ReactNode;
  children: ReactNode;
  /** Minimum width of the scrolled content. The region itself stays within the card. */
  minWidth?: number | string;
}

/**
 * The scroll region's accessible name: `name`, or else a string `title`. A card with a non-string
 * title (or none) must pass `name`; leaving it out is a compile error (TMMAPP-0097 B17).
 */
type TableCardName = { title: string; name?: string } | { title?: ReactNode; name: string };

type TableCardProps = TableCardBase & TableCardName;

function regionName(name: string | undefined, title: ReactNode): string | undefined {
  if (name !== undefined && name !== '') return name;
  return typeof title === 'string' ? title : undefined;
}

export function TableCard({
  title,
  heading = 'h2',
  subtitle,
  toolbar,
  footnote,
  children,
  minWidth,
  name,
}: TableCardProps) {
  const tokens = resolveTokens(useTheme());
  const label = regionName(name, title);
  // The card header section (TMMAPP-0099, visual contract §6): padding 16 20 over a `line` rule,
  // the title in `h3` type (its level stays `heading`), the sub-line `caption` in `text2`. The
  // table runs to the card's edges below it.
  return (
    <Paper sx={{ p: 0, overflow: 'hidden' }}>
      {(title !== undefined || toolbar !== undefined) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          data-card-header=""
          sx={{
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            p: '16px 20px',
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title !== undefined && title !== null && title !== '' && (
              <Typography variant="h3" component={heading}>
                {title}
              </Typography>
            )}
            {subtitle !== undefined && subtitle !== null && subtitle !== '' && (
              <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {toolbar}
        </Stack>
      )}
      <Box
        tabIndex={0}
        role="region"
        aria-label={label}
        sx={{
          overflowX: 'auto',
          minWidth: 0,
          '&:focus-visible': {
            outline: `2px solid ${tokens.focusRing}`,
            outlineOffset: -2,
          },
        }}
      >
        <Box sx={{ minWidth }}>{children}</Box>
      </Box>
      {footnote !== undefined && footnote !== null && footnote !== '' && (
        <Typography variant="body2" sx={{ p: '12px 20px' }}>
          {footnote}
        </Typography>
      )}
    </Paper>
  );
}
